import React from 'react';
import {
  AttributeDataType,
  type ListingCategoryAttribute,
  type ListingAttributeFilter,
} from '../types';

// ─────────────────────────────────────────────────────────────────────────────
//  ListingAttributeFilters
//  The category-specific half of the search filter panel. Appears only once a category is
//  selected, because the facets ARE that category's definitions — with none chosen there is
//  nothing sensible to offer beyond the global price/condition/distance filters.
//
//  Enum attributes become multi-select chips (OR within the attribute); Number and Year
//  become a min/max pair. Text attributes are not offered: free text filters badly.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  definitions: ListingCategoryAttribute[];
  filters: ListingAttributeFilter[];
  onChange: (filters: ListingAttributeFilter[]) => void;
  loading?: boolean;
}

/**
 * Drops filters whose key the new category does not define, keeping the rest.
 *
 * This is what makes switching Vans → Cars keep a Make/Year filter (both categories define
 * them, and AttrKey is a global vocabulary so they mean the same thing) while switching to
 * Furniture discards them. The create form applies the identical rule to typed values — the
 * only difference is that it tells the seller, because losing typed input silently would not
 * be acceptable, whereas a filter is cheap to re-apply.
 */
export function carryOverFilters(
  filters: ListingAttributeFilter[],
  nextDefinitions: ListingCategoryAttribute[],
): ListingAttributeFilter[] {
  const allowed = new Set(nextDefinitions.filter(d => d.isFilterable).map(d => d.attrKey));
  return filters.filter(f => allowed.has(f.key));
}

/** Serialises for the query string. Empty means "send nothing", not "send []". */
export function encodeAttributeFilters(filters: ListingAttributeFilter[]): string | undefined {
  const active = filters.filter(
    f => (f.values && f.values.length > 0) || f.min != null || f.max != null);
  return active.length > 0 ? JSON.stringify(active) : undefined;
}

export function ListingAttributeFilters({ definitions, filters, onChange, loading }: Props) {
  if (loading) {
    return (
      <div data-testid="attribute-filters-loading" className="space-y-3">
        {[0, 1].map(i => (
          <div key={i} className="animate-pulse">
            <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
            <div className="h-8 w-full bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const filterable = definitions.filter(
    d => d.isFilterable && d.dataType !== AttributeDataType.Text);
  if (filterable.length === 0) return null;

  const find = (key: string) => filters.find(f => f.key === key);

  const update = (key: string, next: Partial<ListingAttributeFilter> | null) => {
    const others = filters.filter(f => f.key !== key);
    if (next === null) { onChange(others); return; }

    const merged: ListingAttributeFilter = { key, ...find(key), ...next };
    const isEmpty =
      (!merged.values || merged.values.length === 0) && merged.min == null && merged.max == null;
    onChange(isEmpty ? others : [...others, merged]);
  };

  return (
    <div data-testid="attribute-filters-section" className="space-y-4">
      <h4 className="text-sm font-semibold text-gray-900">Refine by details</h4>

      {filterable.map(def => (
        <div key={def.attrKey}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {def.label}
            {def.unit && <span className="text-gray-400 font-normal"> ({def.unit})</span>}
          </label>

          {def.dataType === AttributeDataType.Enum ? (
            <div className="flex flex-wrap gap-2">
              {def.options.map(option => {
                const selected = find(def.attrKey)?.values?.includes(option) ?? false;
                return (
                  <button
                    key={option}
                    type="button"
                    data-testid={`attribute-filter-${def.attrKey}-${option.toLowerCase().replace(/\s+/g, '-')}`}
                    aria-pressed={selected}
                    onClick={() => {
                      const current = find(def.attrKey)?.values ?? [];
                      const values = selected
                        ? current.filter(v => v !== option)
                        : [...current, option];
                      update(def.attrKey, { values });
                    }}
                    className={`px-3 py-1 rounded-full text-sm border transition ${
                      selected
                        ? 'bg-emerald-700 text-white border-emerald-700'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-500'
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          ) : def.dataType === AttributeDataType.Bool ? (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                data-testid={`attribute-filter-${def.attrKey}`}
                className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                checked={find(def.attrKey)?.values?.includes('true') ?? false}
                onChange={e => update(def.attrKey, { values: e.target.checked ? ['true'] : [] })}
              />
              Only show {def.label.toLowerCase()}
            </label>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                data-testid={`attribute-filter-${def.attrKey}-min`}
                placeholder="Min"
                value={find(def.attrKey)?.min ?? ''}
                onChange={e => update(def.attrKey, {
                  min: e.target.value === '' ? undefined : Number(e.target.value),
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
              <span className="text-gray-400">–</span>
              <input
                type="number"
                inputMode="numeric"
                data-testid={`attribute-filter-${def.attrKey}-max`}
                placeholder="Max"
                value={find(def.attrKey)?.max ?? ''}
                onChange={e => update(def.attrKey, {
                  max: e.target.value === '' ? undefined : Number(e.target.value),
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>
          )}
        </div>
      ))}

      {filters.length > 0 && (
        <button
          type="button"
          data-testid="attribute-filters-clear"
          onClick={() => onChange([])}
          className="text-sm text-emerald-700 hover:underline"
        >
          Clear detail filters
        </button>
      )}
    </div>
  );
}